// <title> и description профиля /tutors/<slug> на языке страницы (16.10.2026):
//   title — «{Имя} — {предмет} repetitoru, {район | город | onlayn} | Yolutap»,
//     несколько предметов — главный (первый) + «və s. / и др. / etc.», ≤ 60
//     где можно: сначала уходит « | Yolutap», потом место;
//   description — с 28.10.2026 первые строки «о себе» (≥ ABOUT_LEAD_MIN знаков,
//     без ссылок и номеров телефонов), иначе предметы, район/город и метро,
//     классы, цена — только то, что есть в анкете; ≤ 155. Строится на сервере из данных loader
// (tutors-map-head.ts). Анкеты нет — общий title карты.
// Проверка — scripts/verify-tutor-slug-markdown.mts.
import { MAP_COPY } from "./map-copy";
import type { SiteLang } from "@/components/site/SiteChrome";
import { BAKU_CITY, DISTRICTS, GRADES, METRO, labelOf, subjectItemsFor } from "@/data/map-taxonomy";
import { DISTRICT_LABELS } from "@/lib/map-places";
import { priceText } from "@/lib/price-format";
import { parseAbout, type AboutNode } from "@/lib/partner-about";
import type { PublicPartner } from "@/lib/map-public.functions";

type MetaPartner = Pick<
  PublicPartner,
  "name" | "kind" | "subjects" | "address_mode" | "district" | "metro" | "travel_districts" | "city" | "city_name"
> &
  Partial<Pick<PublicPartner, "grades" | "price_azn" | "price_unit" | "lessons_per_unit" | "lesson_minutes" | "price_confirmed" | "about">>;

const KIND: Record<SiteLang, Record<PublicPartner["kind"], string>> = {
  az: { tutor: "repetitor", club: "dərnək" },
  ru: { tutor: "репетитор", club: "кружок" },
  en: { tutor: "tutor", club: "club" },
};

/** Хвост description: «Yolutap xəritəsində repetitor.» и т. п. */
const TAIL: Record<SiteLang, Record<PublicPartner["kind"], string>> = {
  az: { tutor: "Yolutap xəritəsində repetitor.", club: "Yolutap xəritəsində dərnək." },
  ru: { tutor: "Репетитор на карте Yolutap.", club: "Кружок на карте Yolutap." },
  en: { tutor: "Tutor on the Yolutap map.", club: "Club on the Yolutap map." },
};

export const DESCRIPTION_MAX = 155;
export const TITLE_MAX = 60;

const BAKU_NAME: Record<SiteLang, string> = { az: "Bakı", ru: "Баку", en: "Baku" };
const ONLINE: Record<SiteLang, string> = { az: "onlayn", ru: "онлайн", en: "online" };
const MORE: Record<SiteLang, string> = { az: "və s.", ru: "и др.", en: "etc." };
const PRICE: Record<SiteLang, string> = { az: "Qiymət", ru: "Цена", en: "Price" };
/** Добивка description до ~140 знаков — правда о любом профиле, без выдумок. */
const EXTRA: Record<SiteLang, string> = {
  az: "Əlaqə və ünvan — profildə.",
  ru: "Контакты и адрес — в профиле.",
  en: "Contacts and location on the profile.",
};
const DESCRIPTION_MIN = 140;

/** «репетитор: математика» — у русских подписей строчная, кроме IELTS, SAT и т. п. */
const ruLower = (s: string) => (s.length > 1 && s[1] === s[1].toLowerCase() ? s[0].toLowerCase() + s.slice(1) : s);

/** «Riyaziyyat və s. repetitoru» / «репетитор: математика и др.» / «Maths etc. tutor». */
function whoLine(p: MetaPartner, lang: SiteLang, subj: string | null, more: boolean): string {
  if (!subj) return KIND[lang][p.kind];
  const x = more ? `${subj} ${MORE[lang]}` : subj;
  if (lang === "az") return p.kind === "tutor" ? `${x} repetitoru` : `${x} dərnəyi`;
  if (lang === "ru") return `${KIND.ru[p.kind]}: ${more ? `${ruLower(subj)} ${MORE.ru}` : ruLower(subj)}`;
  return `${x} ${KIND.en[p.kind]}`;
}

/** Место для title: район Баку у пина, иначе город; онлайн — «onlayn». */
function titlePlace(p: MetaPartner, lang: SiteLang): string {
  if (p.address_mode === "online") return ONLINE[lang];
  const oc = otherCity(p, lang);
  if (oc) return oc;
  if (p.address_mode === "building" && p.district) return labelOf(DISTRICT_LABELS, p.district, lang);
  return BAKU_NAME[lang];
}

const metroWord = (lang: SiteLang, name: string) =>
  lang === "az" ? `metro ${name}` : lang === "ru" ? `м. ${name}` : `${name} metro`;

/** Город вне Баку (null — Баку или онлайн): тогда он вместо района и метро. Подпись — из map_cities. */
const otherCity = (p: MetaPartner, lang: SiteLang): string | null =>
  p.address_mode !== "online" && p.city && p.city !== BAKU_CITY ? (p.city_name?.[lang] ?? p.city) : null;

function placeLine(p: MetaPartner, lang: SiteLang): string {
  const c = MAP_COPY[lang];
  if (p.address_mode === "online") return c.online;
  const oc = otherCity(p, lang);
  if (oc) return p.address_mode === "travel" ? `${c.travelsTo}: ${oc}` : oc;
  if (p.address_mode === "travel")
    return p.travel_districts.length
      ? `${c.travelsTo}: ${p.travel_districts.map((d) => labelOf(DISTRICT_LABELS, d, lang)).join(", ")}`
      : "";
  const d = p.district ? labelOf(DISTRICT_LABELS, p.district, lang) : "";
  const m =
    p.metro && METRO.some((x) => x.code === p.metro)
      ? metroWord(lang, labelOf(METRO, p.metro, lang))
      : "";
  return [d, m].filter(Boolean).join(", ");
}

/** «О себе» короче — description по-старому, из данных анкеты. */
export const ABOUT_LEAD_MIN = 60;
/** Похоже на номер телефона: 7+ цифр с пробелами, скобками, дефисами. */
const PHONE_LIKE = /\+?\d[\d\s().-]{5,}\d/g;

const plain = (nodes: AboutNode[]): string =>
  nodes.map((n) => (n.t === "text" ? n.v : n.t === "link" ? "" : plain(n.c))).join("");

/**
 * Начало «о себе» для description: текст строк подряд, без ссылок (кнопки
 * соцсетей и адреса) и без номеров телефонов, ≤ DESCRIPTION_MAX по слову.
 */
export function aboutLead(about: string | null | undefined): string {
  if (!about) return "";
  const text = parseAbout(about)
    .lines.map(plain)
    .join(" ")
    .replace(PHONE_LIKE, " ")
    .replace(/\s+/g, " ")
    .replace(/\s+([.,;:!?])/g, "$1")
    .trim();
  if (text.length <= DESCRIPTION_MAX) return text;
  const cut = text.slice(0, DESCRIPTION_MAX - 1);
  const sp = cut.lastIndexOf(" ");
  return (sp > DESCRIPTION_MAX * 0.6 ? cut.slice(0, sp) : cut).replace(/[\s.,;:—–-]+$/, "") + "…";
}

export function partnerMeta(
  p: MetaPartner,
  lang: SiteLang,
): { title: string; description: string } {
  const items = subjectItemsFor(p.kind);
  // Пояснения в скобках («(dərs hazırlığı)») в title и description не нужны — коротко.
  const subjects = p.subjects.map((s) => labelOf(items, s, lang).replace(/\s*\([^)]*\)/g, ""));

  const who = whoLine(p, lang, subjects[0] ?? null, subjects.length > 1);
  const place = titlePlace(p, lang);
  // Не влезает в 60 — по очереди: без « | Yolutap», без места, без «və s.»,
  // и последним — вид вместо предмета.
  const one = whoLine(p, lang, subjects[0] ?? null, false);
  const variants = [
    `${p.name} — ${who}, ${place} | Yolutap`,
    `${p.name} — ${who}, ${place}`,
    `${p.name} — ${who}`,
    `${p.name} — ${one}`,
    `${p.name} — ${KIND[lang][p.kind]}, ${place} | Yolutap`,
    `${p.name} — ${KIND[lang][p.kind]}, ${place}`,
    `${p.name} — ${KIND[lang][p.kind]}`,
  ];
  const title = variants.find((t) => t.length <= TITLE_MAX) ?? variants[variants.length - 1];

  const grades = (p.grades ?? []).map((g) => labelOf(GRADES, g, lang)).join(", ");
  const price =
    p.price_azn !== undefined && p.price_azn !== null
      ? priceText({ kind: p.kind, price_azn: p.price_azn, price_unit: p.price_unit ?? "lesson", lessons_per_unit: p.lessons_per_unit, lesson_minutes: p.lesson_minutes, price_confirmed: p.price_confirmed }, lang).main
      : null;
  // «О себе» есть и не совсем короткое — description из его начала (28.10.2026).
  const lead = aboutLead(p.about);
  if (lead.length >= ABOUT_LEAD_MIN) return { title, description: lead };
  const line = placeLine(p, lang);
  const build = (subj: string[], withGrades: boolean) => {
    const head = [subj.join(", "), line].filter(Boolean).join(" — ");
    const parts = [head, withGrades ? grades : "", price ? `${PRICE[lang]}: ${price}` : "", TAIL[lang][p.kind].replace(/\.$/, "")].filter(Boolean);
    return parts.join(". ") + ".";
  };
  // Длинный список предметов — убираем с конца, пока не влезет; потом классы.
  let n = subjects.length;
  let withGrades = true;
  let description = build(subjects, withGrades);
  while (description.length > DESCRIPTION_MAX && n > 1) description = build([...subjects.slice(0, --n), "…"], withGrades);
  if (description.length > DESCRIPTION_MAX && grades) description = build(subjects.slice(0, Math.max(n, 1)).concat(n < subjects.length ? ["…"] : []), (withGrades = false));
  if (description.length > DESCRIPTION_MAX) description = description.slice(0, DESCRIPTION_MAX - 1).trimEnd() + "…";
  else if (description.length < DESCRIPTION_MIN && description.length + 1 + EXTRA[lang].length <= DESCRIPTION_MAX)
    description = `${description} ${EXTRA[lang]}`;
  return { title, description };
}
